import { Link, createFileRoute, useRouter, notFound } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { toast } from "sonner";
import { fetchProduct, fetchProducts, formatMoney, type ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { ProductCard } from "@/components/ProductCard";
import { Loader2, ShoppingBag, X, ZoomIn, Check, ChevronLeft, ChevronRight, Zap, Heart } from "lucide-react";
import { useT, useLocale } from "@/lib/i18n";
import { productJsonLd, breadcrumbJsonLd, absUrl, SITE_URL, hreflangLinks } from "@/lib/seo";
import { trackRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { useLogProductView } from "@/hooks/useLogProductView";
import { trackProductView, trackStickyCtaClick } from "@/lib/analytics";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CordSelector, cordKind, isCordEligible, useCordAddon } from "@/components/product/CordOption";
import { useWishlist } from "@/hooks/useWishlist";
import { ProductGuide } from "@/components/product/ProductGuide";
import { MuranoFilmstrip, MuranoHeroGallery, isMuranoLamp } from "@/components/product/MuranoGallery";
import { findProductGuide } from "@/lib/product-guides";
import { ProductSpecs } from "@/components/product/ProductSpecs";
import { muranoCeilingSpecs } from "@/lib/murano-specs";

export const Route = createFileRoute("/products/$handle")({
  loader: async ({ params }) => {
    // Fetch product + related in parallel. Related items are non-critical:
    // if they fail we still render the page rather than throwing to the
    // errorComponent. This eliminates the intermittent "Vi kunne ikke hente
    // varen" caused by a transient Storefront glitch on the related call.
    const [productRes, relatedRes] = await Promise.allSettled([
      fetchProduct(params.handle),
      fetchProducts({ first: 4 }),
    ]);

    if (productRes.status === "rejected") {
      console.error("[product loader] fetchProduct threw", {
        handle: params.handle,
        reason: productRes.reason,
      });
      throw productRes.reason;
    }
    const product = productRes.value;
    if (!product) {
      console.warn("[product loader] product not found", { handle: params.handle });
      throw notFound();
    }

    let related: Awaited<ReturnType<typeof fetchProducts>> = [];
    if (relatedRes.status === "fulfilled") {
      related = relatedRes.value;
    } else {
      console.warn("[product loader] fetchProducts (related) failed — rendering without", {
        handle: params.handle,
        reason: relatedRes.reason,
      });
    }

    return { product, related };
  },
  head: ({ loaderData, params }) => {
    const p = loaderData?.product;
    const handle = params?.handle ?? "";
    const img = p?.images?.edges?.[0]?.node?.url;
    const url = `${SITE_URL}/products/${handle}`;

    const trimMeta = (s: string | undefined, max = 155): string => {
      if (!s) return "";
      const clean = s.replace(/\s+/g, " ").trim();
      if (clean.length <= max) return clean;
      const cut = clean.slice(0, max);
      const lastSpace = cut.lastIndexOf(" ");
      return (lastSpace > 80 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
    };
    const description =
      trimMeta(p?.description) ||
      `${p?.title ?? "Vintage fund"} — håndplukket vintage hos Farveladeland. Sendes til hele verden.`;

    // Pin priceValidUntil to a stable year-end date so the JSON-LD payload is
    // identical between SSR and client hydration. Using Date.now() here caused
    // occasional string drift across midnight.
    const validUntil = `${new Date().getUTCFullYear() + 1}-12-31`;

    const productLd = p
      ? {
          ...productJsonLd({
            name: p.title,
            description,
            url,
            image: p.images.edges.map((e) => absUrl(e.node.url)),
            sku: p.handle,
            price: p.priceRange.minVariantPrice.amount,
            currency: p.priceRange.minVariantPrice.currencyCode,
            available: p.availableForSale,
          }),
          offers: {
            "@type": "Offer",
            url,
            priceCurrency: p.priceRange.minVariantPrice.currencyCode,
            price: p.priceRange.minVariantPrice.amount,
            priceValidUntil: validUntil,
            availability: p.availableForSale
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
            itemCondition: "https://schema.org/UsedCondition",
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: {
                "@type": "MonetaryAmount",
                value: "0",
                currency: p.priceRange.minVariantPrice.currencyCode,
              },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "DK" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 5, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 3, unitCode: "DAY" },
              },
            },
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "DK",
              returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
              merchantReturnDays: 14,
              returnMethod: "https://schema.org/ReturnByMail",
              returnFees: "https://schema.org/FreeReturn",
            },
          },
        }
      : null;
    const crumb = p
      ? breadcrumbJsonLd([
          { name: "Forsiden", url: SITE_URL },
          { name: "Kollektioner", url: `${SITE_URL}/collections` },
          { name: p.title, url },
        ])
      : null;

    const hreflang = hreflangLinks({ path: `/products/${handle}`, locale: "da" });

    return {
      meta: [
        { title: `${p?.title ?? "Vare"} — Farveladeland` },
        { name: "description", content: description },
        { property: "og:title", content: `${p?.title ?? "Vare"} — Farveladeland` },
        { property: "og:type", content: "product" },
        { property: "og:description", content: description },
        { property: "og:locale", content: "da_DK" },
        { property: "og:locale:alternate", content: "en_US" },
        ...(img ? [{ property: "og:image", content: img }] : []),
        ...(img ? [{ name: "twitter:image", content: img }] : []),
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflang],
      scripts: [
        ...(productLd ? [{ type: "application/ld+json", children: JSON.stringify(productLd) }] : []),
        ...(crumb ? [{ type: "application/ld+json", children: JSON.stringify(crumb) }] : []),
      ],
    };
  },
  component: ProductPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
      const { t } = useT();
      /* simons changes start */
      console.error("[Product route errorComponent]", error);
      console.error("[Product route stack]", error instanceof Error ? error.stack : error);
      /* simons changes end */
    return (
      <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
        <h1 className="font-display text-5xl">{t("product.error.title")}</h1>
        {import.meta.env.DEV && <p className="mt-4 text-sm text-muted-foreground">{error.message}</p>}
        <button
          onClick={() => { router.invalidate(); reset(); }}
          className="mt-8 rounded-full bg-foreground px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-background hover:bg-terracotta"
        >
          {t("product.error.cta")}
        </button>
      </div>
    );
  },
  notFoundComponent: () => {
    const { t } = useT();
    return (
      <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
        <p className="eyebrow">{t("product.notFound.eyebrow")}</p>
        <h1 className="mt-4 font-display text-5xl">{t("product.notFound.title")}</h1>
        <Link
          to="/collections"
          className="mt-8 inline-flex rounded-full bg-foreground px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-background hover:bg-terracotta"
        >
          {t("product.notFound.cta")}
        </Link>
      </div>
    );
  },
});


function ProductPage() {
  const { product, related } = Route.useLoaderData();
  const { t } = useT();
  const locale = useLocale();


  const variants = product.variants.edges;
  // Pre-select the first AVAILABLE variant so a sold-out variant isn't the
  // default. Falls back to index 0 if none are available.
  const initialVariantIndex = useMemo(() => {
    const i = variants.findIndex(
      (v: { node: { availableForSale: boolean } }) => v.node.availableForSale,
    );
    return i === -1 ? 0 : i;
  }, [variants]);
  const [variantIndex, setVariantIndex] = useState(initialVariantIndex);
  const [imgIndex, setImgIndex] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [showSoldOut, setShowSoldOut] = useState(false);
  const addItem = useCartStore((s) => s.addItem);
  const setCartOpen = useCartStore((s) => s.setOpen);
  const isLoading = useCartStore((s) => s.isLoading);
  const cartItems = useCartStore((s) => s.items);

  const variant = variants[variantIndex]?.node;
  const images = product.images.edges;
  const heroImg = images[imgIndex]?.node;
  const restImages = images.filter((_: unknown, i: number) => i !== imgIndex);
  
  const [lastCollection, setLastCollection] = useState<{ handle: string; name: string } | null>(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("fl:lastCollection");
      if (raw) setLastCollection(JSON.parse(raw));
    } catch {}
  }, []);

  const guideConfig = findProductGuide(product);
  const muranoLamp = isMuranoLamp(product.productType);
  const specGroups = useMemo(
    () =>
      /murano loftlamp/i.test(product.productType ?? "")
        ? muranoCeilingSpecs(product.description ?? "", product.title)
        : [],
    [product.productType, product.description, product.title],
  );
  const cordEligible = isCordEligible(product.productType, product.title);
  const cordAddon = useCordAddon(cordKind(product.productType, product.title));

  const { has: hasWish, toggle: toggleWish } = useWishlist();
  const wished = hasWish(product.handle);
  const handleWishlist = () => {
    const adding = !wished;
    toggleWish(product.handle);
    toast(adding ? t("wishlist.toastAdded") : t("wishlist.toastRemoved"), {
      description: product.title,
      action: adding
        ? {
            label: t("wishlist.toastAction"),
            onClick: () => {
              if (typeof window !== "undefined") window.location.href = "/onskeliste";
            },
          }
        : undefined,
    });
  };

  const goPrev = () => setImgIndex((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setImgIndex((i) => (i + 1) % images.length);

  // Mobile gallery — Embla. Uses a transformed track inside an overflow:hidden
  // viewport so pinch-zoom out can't reveal sibling slides side-by-side.
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start", containScroll: "trimSnaps" });
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setImgIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi]);
  useEffect(() => {
    if (!emblaApi) return;
    if (emblaApi.selectedScrollSnap() !== imgIndex) emblaApi.scrollTo(imgIndex);
  }, [emblaApi, imgIndex]);
  const scrollToImg = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);

  const touchStartX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null || images.length < 2) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? goNext : goPrev)();
    touchStartX.current = null;
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && zoomOpen) setZoomOpen(false);
      if (images.length < 2) return;
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomOpen, images.length]);

  useEffect(() => { trackRecentlyViewed(product.handle); }, [product.handle]);
  useLogProductView(product.handle);
  useScrollDepth(product.handle);

  useEffect(() => {
    trackProductView({
      id: product.id, handle: product.handle, title: product.title,
      price: product.priceRange.minVariantPrice, variantId: variant?.id,
    });
  }, [product.id, product.handle, product.title, product.priceRange.minVariantPrice, variant?.id]);

  const handleAdd = async () => {
    if (!variant) return;
    const productWrapper: ShopifyProduct = { node: product };
    await addItem({
      product: productWrapper,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      quantityAvailable: variant.quantityAvailable ?? null,
      selectedOptions: variant.selectedOptions,
    });
    if (cordEligible && cordAddon.product && cordAddon.selected.length > 0) {
      for (const cv of cordAddon.selected) {
        await addItem({
          product: { node: cordAddon.product } as ShopifyProduct,
          variantId: cv.id,
          variantTitle: cv.title,
          price: cv.price,
          quantity: 1,
          quantityAvailable: cv.quantityAvailable ?? null,
          selectedOptions: cv.selectedOptions,
        });
      }
    }
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1800);
  };

  const [expressLoading, setExpressLoading] = useState(false);
  const handleExpressBuy = async () => {
    if (!variant || expressLoading) return;
    setExpressLoading(true);
    const productWrapper: ShopifyProduct = { node: product };
    try {
      await addItem({
        product: productWrapper,
        variantId: variant.id,
        variantTitle: variant.title,
        price: variant.price,
        quantity: 1,
        quantityAvailable: variant.quantityAvailable ?? null,
        selectedOptions: variant.selectedOptions,
      });
      const url = useCartStore.getState().checkoutUrl;
      if (url) { window.location.href = url; return; }
      setCartOpen(true);
    } finally {
      setExpressLoading(false);
    }
  };

  // --- Copy (locale-aware, kept inline so we don't grow the i18n dict) ----
  const copy = locale === "en"
    ? {
        descriptionTitle: "About this piece",
        relatedTitle: "Others also viewed",
        disclaimer: "We trade in vintage and second-hand. Expect normal signs of use — we always disclose chips, knocks and other defects in the description.",
      }
    : {
        descriptionTitle: "Om varen",
        relatedTitle: "Andre kiggede også på",
        disclaimer: "Vi handler med vintage og genbrug. Forvent almindelige brugsspor — vi informerer altid om skår, slag og andre defekter i beskrivelsen.",
      };

  return (
    <>
      {/* SECTION 1 — HERO */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-20">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          {lastCollection ? (
            <Link
              to="/collections/$handle"
              params={{ handle: lastCollection.handle }}
              className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground hover:text-terracotta"
            >
              ← {locale === "en" ? `Back to ${lastCollection.name}` : `Tilbage til ${lastCollection.name}`}
            </Link>
          ) : (
            <Link
              to="/collections"
              className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground hover:text-terracotta"
            >
              {t("product.back")}
            </Link>
          )}

          <div className="mt-8 grid gap-10 md:mt-12 md:grid-cols-12 md:gap-16">
            {/* Hero image — large, no clutter */}
            <div className="md:col-span-7">
              {/* Mobile: Embla carousel of ALL images with dots.
                  Embla uses a transform-based track inside overflow:hidden,
                  so pinch-zoom out cannot reveal sibling slides. */}
              <div className="md:hidden">
                <div className="relative">
                  <div className="-mx-5 overflow-hidden bg-muted" ref={emblaRef}>
                    <div className="flex touch-pan-y">
                      {images.map((im: { node: { url: string; altText: string | null } }, i: number) => (
                        <button
                          key={im.node.url}
                          type="button"
                          onClick={() => { setImgIndex(i); setZoomOpen(true); }}
                          className="relative aspect-[4/5] min-w-0 shrink-0 grow-0 basis-full overflow-hidden bg-muted"
                          aria-label={t("product.zoom")}
                        >
                          <img
                            src={im.node.url}
                            alt={im.node.altText ?? product.title}
                            fetchPriority={i === 0 ? "high" : "low"}
                            loading={i === 0 ? "eager" : "lazy"}
                            decoding="async"
                            className="h-full w-full object-contain"
                            draggable={false}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  {images.length > 1 && (
                    <span
                      aria-hidden
                      className="pointer-events-none absolute bottom-3 right-3 rounded-full bg-background/80 px-2 py-0.5 text-[10px] tracking-[0.12em] text-foreground/70 backdrop-blur"
                    >
                      {imgIndex + 1} / {images.length}
                    </span>
                  )}
                </div>
                {images.length > 1 && (
                  <div className="mt-3 flex items-center justify-center gap-1.5">
                    {images.map((_: unknown, i: number) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => scrollToImg(i)}
                        aria-label={`Gå til billede ${i + 1}`}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          i === imgIndex ? "w-5 bg-foreground" : "w-1.5 bg-foreground/25"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {muranoLamp ? (
                <MuranoHeroGallery
                  images={images.map((e: { node: { url: string; altText: string | null } }) => e.node)}
                  index={imgIndex}
                  onIndexChange={setImgIndex}
                  onZoom={() => setZoomOpen(true)}
                  title={product.title}
                  zoomLabel={t("product.zoom")}
                  locale={locale}
                />
              ) : (
                <>
              {/* Desktop: single hero w/ hover zoom */}
              <div
                className="group relative hidden aspect-[4/5] w-full overflow-hidden bg-muted md:block"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
              >
                <button
                  type="button"
                  onClick={() => setZoomOpen(true)}
                  className="absolute inset-0 block h-full w-full"
                  aria-label={t("product.zoom")}
                >
                  {heroImg && (
                    <img
                      key={heroImg.url}
                      src={heroImg.url}
                      alt={heroImg.altText ?? product.title}
                      fetchPriority="high"
                      className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.015]"
                    />
                  )}
                </button>
                <span className="pointer-events-none absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-background/85 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-foreground/80 opacity-0 transition-opacity group-hover:opacity-100">
                  <ZoomIn className="h-3 w-3" /> {t("product.zoom")}
                </span>
              </div>
                </>
              )}
            </div>

            {/* Info — calm, hierarchy first */}
            <div className="md:col-span-5 md:pt-6">
              <div className="flex items-start justify-between gap-4">
                <h1 className="font-display text-[clamp(2rem,4vw,3.4rem)] leading-[1.02] tracking-tight">
                  {product.title}
                </h1>
                <button
                  type="button"
                  onClick={handleWishlist}
                  aria-label={wished ? t("wishlist.remove") : t("wishlist.add")}
                  aria-pressed={wished}
                  title={wished ? t("wishlist.remove") : t("wishlist.add")}
                  className={`mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-all hover:scale-105 ${
                    wished
                      ? "border-terracotta bg-terracotta/10 text-terracotta"
                      : "border-border text-foreground/70 hover:border-foreground hover:text-foreground"
                  } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta`}
                >
                  <Heart
                    className="h-5 w-5"
                    fill={wished ? "currentColor" : "none"}
                    strokeWidth={1.6}
                  />
                </button>
              </div>

              {(() => {
                const qty = variant?.quantityAvailable;
                if (!variant?.availableForSale) {
                  return (
                    <p className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
                      {locale === "en" ? "Sold out" : "Udsolgt"}
                    </p>
                  );
                }
                // Only show a stock line when we have a precise number from
                // Shopify (inventory tracking on). Never fall back to a vague
                // "in stock" — it caused confusion on the product page.
                if (typeof qty !== "number" || qty <= 0) return null;
                const label = locale === "en"
                  ? qty === 1 ? "1 in stock" : `${qty} in stock`
                  : qty === 1 ? "1 på lager" : `${qty} på lager`;
                return (
                  <p className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
                    {label}
                  </p>
                );
              })()}

              {(() => {
                const currentPrice = variant?.price ?? product.priceRange.minVariantPrice;
                const compareAt = variant?.compareAtPrice ?? null;
                const onSale = !!compareAt && parseFloat(compareAt.amount) > parseFloat(currentPrice.amount);
                return (
                  <div className="mt-4 flex items-baseline gap-3">
                    <p className={`font-display text-2xl ${onSale ? "text-terracotta" : "text-foreground"}`}>
                      {formatMoney(currentPrice)}
                    </p>
                    {onSale && compareAt && (
                      <>
                        <span className="text-base text-muted-foreground line-through">
                          {formatMoney(compareAt)}
                        </span>
                        <span className="rounded-full bg-terracotta/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-terracotta">
                          {t("badge.sale")}
                        </span>
                      </>
                    )}
                  </div>
                );
              })()}

              {product.description && (
                <div className="mt-6 max-w-[42ch] whitespace-pre-line text-[15px] leading-relaxed text-foreground/80">
                  {product.description}
                </div>
              )}


              <p className="mt-5 text-[11px] leading-relaxed text-muted-foreground">
                {copy.disclaimer}
              </p>

              {variants.length > 1 && (
                <div className="mt-8">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                    {t("product.variant")}
                  </p>
                  {(() => {
                    type V = { node: { id: string; title: string; availableForSale: boolean } };
                    const indexed: { v: V; i: number }[] = variants.map((v: V, i: number) => ({ v, i }));
                    const available = indexed.filter((x: { v: V; i: number }) => x.v.node.availableForSale);
                    const soldOut = indexed.filter((x: { v: V; i: number }) => !x.v.node.availableForSale);
                    const allSoldOut = available.length === 0;
                    const visible = allSoldOut || showSoldOut ? indexed : available;
                    const renderBtn = ({ v, i }: { v: V; i: number }) => (
                      <button
                        key={v.node.id}
                        onClick={() => setVariantIndex(i)}
                        disabled={!v.node.availableForSale}
                        className={`rounded-full border px-4 py-2 text-[11px] uppercase tracking-[0.18em] transition-colors ${
                          i === variantIndex
                            ? "border-foreground bg-foreground text-background"
                            : "border-border text-foreground hover:border-foreground"
                        } ${!v.node.availableForSale ? "opacity-40 line-through" : ""}`}
                      >
                        {v.node.title}
                      </button>
                    );
                    return (
                      <>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {visible.map(renderBtn)}
                        </div>
                        {!allSoldOut && soldOut.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowSoldOut((s) => !s)}
                            className="mt-3 text-[10px] uppercase tracking-[0.24em] text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                          >
                            {showSoldOut
                              ? locale === "en" ? "Hide sold out" : "Skjul udsolgte"
                              : locale === "en" ? `Show sold out (${soldOut.length})` : `Vis udsolgte (${soldOut.length})`}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {cordEligible && cordAddon.hasCordOption && (
                <CordSelector {...cordAddon} />
              )}

              {(() => {
                const inCart = cartItems.find((i) => i.variantId === variant?.id)?.quantity ?? 0;
                const stock = variant?.quantityAvailable;
                const atMax = typeof stock === "number" && inCart >= stock;
                const disabled = !variant?.availableForSale || isLoading || atMax;
                return (
                  <>
                    {/* Single primary CTA */}
                    <button
                      onClick={handleAdd}
                      disabled={disabled}
                      className="mt-9 hidden w-full items-center justify-center gap-3 rounded-full bg-foreground px-6 py-4 text-[11px] font-medium uppercase tracking-[0.24em] text-background transition-all hover:bg-terracotta disabled:cursor-not-allowed disabled:opacity-50 sm:inline-flex"
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : justAdded ? (
                        <><Check className="h-4 w-4" /> {t("cart.added")}</>
                      ) : !variant?.availableForSale ? (
                        t("product.sold")
                      ) : atMax ? (
                        locale === "en" ? "Max in cart" : "Maks. i kurv"
                      ) : (
                        <><ShoppingBag className="h-4 w-4" /> {t("product.add")}</>
                      )}
                    </button>
                    {atMax && variant?.availableForSale && (
                      <p className="mt-2 hidden text-[11px] text-muted-foreground sm:block">
                        {locale === "en"
                          ? `You already have all ${stock} in your cart.`
                          : `Du har allerede alle ${stock} i kurven.`}
                      </p>
                    )}
                  </>
                );
              })()}

              {justAdded && (
                <button
                  onClick={() => setCartOpen(true)}
                  className="mt-3 hidden text-[11px] uppercase tracking-[0.22em] text-terracotta hover:text-foreground sm:inline-block"
                >
                  {t("cart.viewCart")} →
                </button>
              )}

              {/* Specifications from the description (Murano ceiling lamps) */}
              {specGroups.length > 0 && <ProductSpecs groups={specGroups} locale={locale} />}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — GALLERY (visual depth) */}
      {!muranoLamp && restImages.length > 0 && (
        <section className="hidden pb-16 md:block md:pb-24">
          <div className="mx-auto max-w-[1100px] px-5 md:px-10">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-10">
              {restImages.slice(0, 5).map(
                (img: { node: { url: string; altText: string | null } }, i: number) => (
                  <button
                    key={img.node.url}
                    onClick={() => { setImgIndex(images.findIndex((x: typeof img) => x.node.url === img.node.url)); setZoomOpen(true); }}
                    className={`group block overflow-hidden bg-muted ${i === 0 ? "aspect-[4/5] md:col-span-2" : "aspect-square"}`}
                  >
                    <img
                      src={img.node.url}
                      alt={img.node.altText ?? product.title}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                    />
                  </button>
                ),
              )}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 4 — UP CLOSE (Murano lamps): editorial filmstrip instead of the image wall */}
      {muranoLamp && images.length > 1 && (
        <MuranoFilmstrip
          images={images.slice(1).map((e: { node: { url: string; altText: string | null } }) => e.node)}
          onOpen={(i) => {
            setImgIndex(i + 1);
            setZoomOpen(true);
          }}
          title={product.title}
          locale={locale}
        />
      )}

      {/* SECTION 5 — GUIDE FINDER (questions → matching blog guides) */}
      {guideConfig && <ProductGuide config={guideConfig} />}

      {/* SECTION 6 — RELATED, curated */}
      {related.length > 0 && (
        <section className="mx-auto max-w-[1300px] px-5 pt-20 pb-24 md:px-10 md:pt-28 md:pb-32">
          <div className="text-center">
            <p className="eyebrow">{copy.relatedTitle}</p>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-x-5 gap-y-12 md:grid-cols-4 md:gap-x-8">
            {related.slice(0, 4).map((p: ShopifyProduct) => (
              <ProductCard key={p.node.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* Spacer so mobile sticky buy-bar never overlaps the last section */}
      <div className="h-24 sm:hidden" aria-hidden />




      {/* Mobile sticky buy-bar */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-3 pt-2.5 backdrop-blur sm:hidden"
        style={{ paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-center gap-2.5">
          {heroImg && (
            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-sm bg-muted">
              <img src={heroImg.url} alt="" aria-hidden className="h-full w-full object-contain" />
            </div>
          )}
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-[11px] text-muted-foreground">{product.title}</p>
            {(() => {
              const currentPrice = variant?.price ?? product.priceRange.minVariantPrice;
              const compareAt = variant?.compareAtPrice ?? null;
              const onSale = !!compareAt && parseFloat(compareAt.amount) > parseFloat(currentPrice.amount);
              return (
                <p className="flex items-baseline gap-1.5 font-display text-[15px]">
                  <span className={onSale ? "text-terracotta" : "text-foreground"}>
                    {formatMoney(currentPrice)}
                  </span>
                  {onSale && compareAt && (
                    <span className="text-[11px] text-muted-foreground line-through">
                      {formatMoney(compareAt)}
                    </span>
                  )}
                </p>
              );
            })()}
          </div>
          {variant?.availableForSale ? (
            <>
              <button
                onClick={() => { trackStickyCtaClick("add_to_cart"); handleAdd(); }}
                disabled={isLoading || expressLoading}
                aria-label={t("product.add")}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-foreground/30 text-foreground transition-colors hover:border-foreground disabled:opacity-50"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" />
                  : justAdded ? <Check className="h-4 w-4" />
                  : <ShoppingBag className="h-4 w-4" />}
              </button>
              <button
                onClick={() => { trackStickyCtaClick("express_buy"); handleExpressBuy(); }}
                disabled={expressLoading || isLoading}
                className="inline-flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-full bg-foreground px-5 text-[11px] font-medium uppercase tracking-[0.18em] text-background transition-colors hover:bg-terracotta disabled:opacity-50"
              >
                {expressLoading ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <><Zap className="h-3.5 w-3.5" />{t("product.expressBuy")}</>}
              </button>
            </>
          ) : (
            <span className="inline-flex h-11 items-center rounded-full border border-border px-4 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              {t("product.sold")}
            </span>
          )}
        </div>
      </div>

      {/* Zoom lightbox */}
      {zoomOpen && heroImg && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-foreground/95 p-4"
          onClick={() => setZoomOpen(false)}
        >
          <button
            type="button"
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-background/15 text-background hover:bg-background/25"
            onClick={(e) => { e.stopPropagation(); setZoomOpen(false); }}
            aria-label={t("product.zoomClose")}
          >
            <X className="h-5 w-5" />
          </button>
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                aria-label={t("product.prevImage")}
                className="absolute left-4 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background/15 text-background hover:bg-background/25"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                aria-label={t("product.nextImage")}
                className="absolute right-4 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background/15 text-background hover:bg-background/25"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
          <img
            src={heroImg.url}
            alt={heroImg.altText ?? product.title}
            className="max-h-[90vh] max-w-[95vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
